import React, {
	useState,
	ChangeEvent,
	useCallback,
	useMemo,
	forwardRef,
	useImperativeHandle
} from "react";
import { ErrorIcon, GoAway } from "../Icons/Interface";

interface CustomTextProps {
	label: string;
	initialValue?: string;
	formatter?: (value: string) => string;
	onChange?: (value: string) => void;
	onValidate?: (value: string) => boolean;
	validationText?: string; // Added validation error message text
}

export interface CustomInputHandle {
	triggerValidate: () => boolean;
}

const CustomInput = forwardRef<CustomInputHandle, CustomTextProps>(
	(
		{
			label,
			initialValue = "",
			formatter: userFormatter = (noop: string) => noop,
			onChange: userOnChange = (noop: string) => {},
			onValidate: userOnValidate = (noop: string) => true,
			validationText = "" // Default to empty string
		},
		ref
	) => {
		const [init] = useState<string>(initialValue);
		const [isClientFailure, setIsClientFailure] = useState<boolean>(false);
		const [invalidMessage, setInvalidMessage] =
			useState<string>(validationText);
		const [value, setValue] = useState<string>(initialValue);
		const [isFocused, setIsFocused] = useState<boolean>(false);
		const [hasTriggeredValidation, setHasTriggeredValidation] =
			useState<boolean>(false);
		const isInitialValue = value === initialValue;
		const [inputId] = useState<string>(
			`input-${Math.floor(Math.random() * 100)}`
		);

		useImperativeHandle(ref, () => ({
			triggerValidate: () => {
				setHasTriggeredValidation(true);
				return onValidate(value);
			}
		}));

		// Safe wrapper functions that only handle errors during user interactions
		const formatter = useCallback(
			(value: string): string => {
				try {
					return userFormatter(value);
				} catch (err) {
					console.log("Client Formatter Threw Exception:", err);
					setInvalidMessage("Call Support - Client Formatter");
					setIsClientFailure(true);
					return value; // Return original value on error
				}
			},
			[userFormatter]
		);

		const onChange = useCallback(
			(value: string): void => {
				try {
					userOnChange(value);
				} catch (err) {
					console.log("Client onChange Threw Exception:", err);
					setInvalidMessage("Call Support - Client Change");
					setIsClientFailure(true);
				}
			},
			[userOnChange]
		);

		const onValidate = useCallback(
			(value: string): boolean => {
				try {
					const isValid = userOnValidate(value);
					return isValid;
				} catch (err) {
					console.log("Client Validate Threw Exception:", err);
					setInvalidMessage("Call Support - Client Validate");
					setIsClientFailure(true);
					return false;
				}
			},
			[userOnValidate]
		);

		// Track if validation is currently failing
		//const isInvalid = !isInitialValue && !onValidate(value);
		const isInvalid = useMemo(() => {
			// Show validation errors if: 1) value changed from initial, OR 2) triggerValidate was called
			return (!isInitialValue || hasTriggeredValidation) && !onValidate(value);
		}, [isInitialValue, hasTriggeredValidation, value, onValidate]);

		const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
			const newValue = formatter(e.target.value);
			setValue(newValue);
			if (onValidate(newValue)) onChange(newValue);
		};

		const handleFocus = (): void => {
			setIsFocused(true);
		};

		const handleBlur = (): void => {
			setIsFocused(false);
		};

		const clearInput = (): void => {
			setValue(init);
			onChange(init);
		};

		const handleKeypress = (
			event: React.KeyboardEvent<HTMLInputElement>
		): void => {
			// console.log(
			// 	`CustomInput Keydown Called  key:[${event.key}] label:[${label}] `
			// );
			if (event.key === "Escape") {
				// console.log(
				// 	`CustomInput Acting on key:[${event.key}] initalValue:[${initialValue}]  label:[${label}] `
				// );

				clearInput();
				event.stopPropagation(); //StopBubbling event as it was handled.
			}
		};

		return (
			<div className="relative w-full mt-4" aria-label="Input Field">
				<div className="relative">
					<input
						id={inputId}
						type="text"
						value={value}
						onKeyDown={handleKeypress}
						onChange={handleChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						className={`w-full px-4 py-3 text-primary bg-transparent border rounded-full cursor-text focus:outline-none ${
							isInvalid
								? "border-error-inactive focus:border-error-active"
								: "border-primary-inactive focus:border-primary-active"
						}`}
						aria-invalid={isInvalid}
					/>

					<label
						htmlFor={inputId}
						className={`absolute text-primary transition-all duration-300 pointer-events-none ${
							isFocused || value
								? "text-sm -top-2.5 left-6 pl-1 pr-1 rounded bg-primary-background"
								: "text-base top-3 left-6 bg-transparent"
						}`}
					>
						{label}
					</label>

					{!isFocused && value && (
						<button
							type="button"
							onClick={clearInput}
							className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-inactive cursor-pointer hover:text-primary-active fill-primary-active"
							aria-label="Clear input"
						>
							{GoAway()}
						</button>
					)}
				</div>

				{/* Validation message container with fixed height */}
				<div
					className={`cursor-default flex items-center justify-start text-sm px-6 mt-1 transition-opacity duration-200 min-h-6 ${
						isInvalid || isClientFailure ? "" : "hidden"
					} ${isFocused ? "text-error-active" : "text-error-inactive"}`}
				>
					<div className="h-4 w-4 mr-2">{ErrorIcon(16)} </div>
					<div>{invalidMessage}</div>
				</div>
			</div>
		);
	}
);

CustomInput.displayName = "CustomInput";

export default CustomInput;
